import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, FormControlLabel, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import './EditMapPage.css';

const BusCheckboxes = ({ 
  selectedElements, 
  handleSelectElement, 
  handleRemoveElement,
  initialSelectedRoutes = [],
  mapLinesData = {}
}) => {
  const [busRoutes, setBusRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loadingRoutes, setLoadingRoutes] = useState({});
  const [selectedZones, setSelectedZones] = useState([]);
  const [hasProcessedInitialRoutes, setHasProcessedInitialRoutes] = useState(false);

  // Definición de las zonas y sus líneas
  const zones = {
    'Norte': ['21', '29', '44', '60', '76', '78', '87', '93', '108', '117', '118', '130', '204', '228', '341', '391', '720', '722', '723'],
    'Sur': ['26','31', '33', '45', '46', '70', '85','98', '100', '115', '119', '150', '164', '179', '188', '271', '295', '299', '324', '384', '421', '501', '524', '527'],
    'Oeste': ['1', '8', '23', '25', '46', '91', '99', '115','132', '135', '150', '244', '302', '303', '390', '461', '462', '463', '464'],
    'Todos': []
  };

  // Cargar todas las rutas de autobuses disponibles
  useEffect(() => {
    const fetchAllBusRoutes = async () => {
      try {
        setInitialLoad(true);
        const response = await axios.get('https://mapa.ne3publicidad.com.ar/api/api/getLineas');
  
        const groupedRoutes = response.data.reduce((acc, route) => {
          const mainRoute = route.linea.replace(/[^0-9]/g, '');
          if (!acc.some(r => r.linea === mainRoute)) {
            acc.push({ 
              linea: mainRoute,
              color: route.color,
              fullLinea: route.linea
            });
          }
          return acc;
        }, []);
  
        setBusRoutes(groupedRoutes);
      } catch (error) {
        console.error("Error fetching bus routes:", error);
      } finally {
        setInitialLoad(false);
      }
    };
    fetchAllBusRoutes();
  }, []);

  // MARCAR RUTAS INICIALES DEL JSON
  useEffect(() => {
    if (busRoutes.length === 0 || initialLoad || hasProcessedInitialRoutes) return;

    // Marcar las rutas iniciales como seleccionadas
    if (initialSelectedRoutes && initialSelectedRoutes.length > 0) {
      console.log("Marcando rutas iniciales del JSON:", initialSelectedRoutes);
      setSelectedRoutes(initialSelectedRoutes);
      setHasProcessedInitialRoutes(true);
    }
  }, [busRoutes, initialLoad, initialSelectedRoutes, hasProcessedInitialRoutes]);

  // Sincronizar con elementos del mapa
  useEffect(() => {
    if (initialLoad) return;

    // Obtener rutas que ya están visibles en el mapa
    const visibleRoutes = selectedElements
      .filter(el => el.type === 'bus' || el.type === 'linea')
      .map(el => {
        const lineName = el.nombre;
        return lineName.replace(/[^0-9]/g, '');
      })
      .filter((value, index, self) => self.indexOf(value) === index);

    console.log("Rutas visibles en el mapa:", visibleRoutes);
    console.log("Rutas seleccionadas en checkboxes:", selectedRoutes);

    // Sincronizar: si una ruta NO está en visibleRoutes pero SÍ en selectedRoutes, desmarcarla
    const routesToDeselect = selectedRoutes.filter(route => 
      !visibleRoutes.includes(route)
    );

    if (routesToDeselect.length > 0) {
      console.log("Desmarcando rutas que ya no están en el mapa:", routesToDeselect);
      setSelectedRoutes(prev => prev.filter(route => !routesToDeselect.includes(route)));
    }

    // Sincronizar: si una ruta SÍ está en visibleRoutes pero NO en selectedRoutes, marcarla
    const routesToSelect = visibleRoutes.filter(route => 
      !selectedRoutes.includes(route)
    );

    if (routesToSelect.length > 0) {
      console.log("Marcando rutas que están en el mapa:", routesToSelect);
      setSelectedRoutes(prev => [...prev, ...routesToSelect]);
    }

  }, [selectedElements, initialLoad]);

  // Función para cargar una ruta
  const fetchBusVariants = async (routeNumber, isZoneSelection = false) => {
    try {
      setLoadingRoutes(prev => ({ ...prev, [routeNumber]: true }));
      
      let variants = [];
      
      // Primero intentar usar los datos del JSON si están disponibles
      const jsonVariants = Object.entries(mapLinesData)
        .filter(([lineName]) => lineName.replace(/[^0-9]/g, '') === routeNumber)
        .flatMap(([lineName, lineData]) => 
          lineData.map(point => ({
            ...point,
            linea: lineName, // Mantener el nombre completo (117, 117B, etc.)
            sequence_order: point.sequence_order || 0,
            color: point.color || '#FF0000'
          }))
        );
      
      if (jsonVariants.length > 0) {
        console.log(`Usando datos del JSON para ruta ${routeNumber}`);
        variants = jsonVariants;
      } else {
        // Si no hay datos en el JSON, cargar desde la API
        console.log(`Cargando ruta ${routeNumber} desde API`);
        const response = await axios.get(`https://mapa.ne3publicidad.com.ar/api/api/getLinea/${routeNumber}`);
        variants = response.data.map(point => ({
          ...point,
          sequence_order: point.sequence_order || 0,
          color: point.color || '#FF0000'
        }));
      }
      
      const routeInfo = busRoutes.find(r => r.linea === routeNumber);
      const routeColor = routeInfo?.color || '#FF0000';
      
      // Agrupar por variante (117, 117B, etc.)
      const variantsByLine = variants.reduce((acc, point) => {
        const lineKey = point.linea;
        if (!acc[lineKey]) {
          acc[lineKey] = [];
        }
        acc[lineKey].push(point);
        return acc;
      }, {});

      // Crear elementos para cada variante
      Object.entries(variantsByLine).forEach(([lineName, lineData]) => {
        const sortedData = [...lineData].sort((a, b) => 
          (a.sequence_order || 0) - (b.sequence_order || 0)
        );
        
        const lineColor = sortedData[0]?.color || routeColor;
        
        handleSelectElement(
          lineName,
          sortedData,
          'bus',
          lineColor
        );
      });

      // Actualizar estado de rutas seleccionadas
      if (!selectedRoutes.includes(routeNumber)) {
        setSelectedRoutes(prev => [...prev, routeNumber]);
      }

      return true;
    } catch (error) {
      console.error(`Error fetching variants for route ${routeNumber}:`, error);
      return false;
    } finally {
      setLoadingRoutes(prev => ({ ...prev, [routeNumber]: false }));
    }
  };

  // Función para eliminar TODAS las variantes de una ruta
  const removeAllRouteVariants = (routeNumber) => {
    console.log(`Eliminando todas las variantes de ruta ${routeNumber}`);
    
    // Buscar todas las variantes de esta ruta
    const variantsToRemove = selectedElements
      .filter(el => {
        if (el.type !== 'bus' && el.type !== 'linea') return false;
        const elRouteNumber = el.nombre.replace(/[^0-9]/g, '');
        return elRouteNumber === routeNumber;
      })
      .map(el => el.nombre);
    
    console.log(`Variantes encontradas para eliminar:`, variantsToRemove);
    
    // Si no encontramos variantes pero la ruta está seleccionada,
    // intentamos eliminar con nombres comunes
    if (variantsToRemove.length === 0) {
      console.log(`No se encontraron variantes. Intentando eliminar ruta base ${routeNumber}...`);
      handleRemoveElement(routeNumber, 'bus');
      handleRemoveElement(`${routeNumber}B`, 'bus');
      handleRemoveElement(`${routeNumber}A`, 'bus');
    } else {
      // Eliminar cada variante encontrada
      variantsToRemove.forEach(variantName => {
        console.log(`Eliminando variante: ${variantName}`);
        handleRemoveElement(variantName, 'bus');
      });
    }
    
    // Actualizar estado
    setSelectedRoutes(prev => prev.filter(r => r !== routeNumber));
  };

  const handleRouteChange = async (routeNumber) => {
    console.log(`Cambiando estado de ruta ${routeNumber}`);
    
    // Si estamos en modo de zona, no permitir cambios individuales
    if (selectedZones.length > 0 && !selectedZones.includes('Todos')) return;

    if (selectedRoutes.includes(routeNumber)) {
      // Desmarcar - eliminar del mapa
      removeAllRouteVariants(routeNumber);
    } else {
      // Marcar - cargar en el mapa
      await fetchBusVariants(routeNumber, false);
    }
  };

  // Efecto para manejar la selección de zonas
  useEffect(() => {
    if (initialLoad || busRoutes.length === 0) return;

    const handleZoneSelection = async () => {
      // Si se seleccionó "Todos", manejar de manera especial
      if (selectedZones.includes('Todos')) {
        // Seleccionar todas las rutas
        const allRoutes = busRoutes.map(route => route.linea);
        const routesToAdd = allRoutes.filter(route => !selectedRoutes.includes(route));
        
        for (const route of routesToAdd) {
          await fetchBusVariants(route, true);
        }
        
        // Desmarcar otras zonas
        setSelectedZones(['Todos']);
        return;
      }

      // Obtener todas las rutas de las zonas seleccionadas
      const zoneRoutes = selectedZones.flatMap(zone => zones[zone]);
      
      // Filtrar rutas que ya están seleccionadas
      const routesToAdd = zoneRoutes.filter(route => 
        !selectedRoutes.includes(route) && 
        busRoutes.some(r => r.linea === route)
      );
      
      // Filtrar rutas que están seleccionadas pero no están en las zonas seleccionadas
      const routesToRemove = selectedRoutes.filter(route => 
        !zoneRoutes.includes(route) && 
        !selectedZones.some(zone => zones[zone].includes(route))
      );
      
      // Eliminar rutas que no pertenecen a las zonas seleccionadas
      routesToRemove.forEach(route => {
        removeAllRouteVariants(route);
      });
      
      // Agregar nuevas rutas de las zonas seleccionadas
      for (const route of routesToAdd) {
        await fetchBusVariants(route, true);
      }
    };

    handleZoneSelection();
  }, [selectedZones, busRoutes]);

  const handleZoneToggle = (zone) => {
    setSelectedZones(prev => {
      if (zone === 'Todos') {
        // Si se selecciona "Todos", deseleccionar otras zonas
        return prev.includes('Todos') ? [] : ['Todos'];
      } else {
        // Si se selecciona otra zona, deseleccionar "Todos" si está seleccionado
        const newZones = prev.includes('Todos') 
          ? [] 
          : [...prev];
        
        if (newZones.includes(zone)) {
          return newZones.filter(z => z !== zone);
        } else {
          return [...newZones, zone];
        }
      }
    });
  };

  if (initialLoad) {
    return (
      <div className="accordion-container">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-button">
            <h3>Recorrido de Buses</h3>
          </AccordionSummary>
          <AccordionDetails className="accordion-details">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return (
    <div className="accordion-container">
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="accordion-button">
          <h3>Recorrido de Buses</h3>
        </AccordionSummary>
        <AccordionDetails className="accordion-details">
          <div className="checkbox-container">
            {busRoutes.map(route => (
              <FormControlLabel
                key={route.linea}
                control={
                  <div
                    className={`custom-checkbox ${selectedRoutes.includes(route.linea) ? 'selected' : ''}`}
                    onClick={() => handleRouteChange(route.linea)}
                    style={{
                      borderColor: route.color || '#FF0000',
                      opacity: loadingRoutes[route.linea] ? 0.7 : 1,
                      cursor: loadingRoutes[route.linea] 
                        ? 'wait' 
                        : (selectedZones.length > 0 && !selectedZones.includes('Todos')) 
                          ? 'not-allowed' 
                          : 'pointer'
                    }}
                    disabled={loadingRoutes[route.linea] || (selectedZones.length > 0 && !selectedZones.includes('Todos'))}
                  >
                    {route.linea}
                    {loadingRoutes[route.linea] && (
                      <CircularProgress size={16} style={{ marginLeft: 8, color: route.color || '#FF0000' }} />
                    )}
                    {selectedRoutes.includes(route.linea) && !loadingRoutes[route.linea] && (
                      <span className="checkmark">✓</span>
                    )}
                  </div>
                }
                label=""
              />
            ))}
          </div>

          {/* Checkboxes de zonas */}
          <div className="zones-container" style={{ 
            marginTop: '20px', 
            paddingTop: '10px', 
            borderTop: '1px solid #ccc',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '10px'
          }}>
            {Object.keys(zones).map(zone => (
              <FormControlLabel
                key={zone}
                control={
                  <div
                    className={`custom-checkbox zone-checkbox ${selectedZones.includes(zone) ? 'selected' : ''}`}
                    onClick={() => handleZoneToggle(zone)}
                    style={{
                      borderColor: '#176A36',
                      backgroundColor: selectedZones.includes(zone) ? '#FFEC00' : '#176A36',
                      color: selectedZones.includes(zone) ? '#000' : '#FFF',
                      padding: '0 15px',
                      cursor: 'pointer',
                      minWidth: '100px'
                    }}
                  >
                    {zone}
                  </div>
                }
                label=""
              />
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default BusCheckboxes;